/**
 * 标签页状态管理
 * 管理多标签页的打开、关闭、切换等操作
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { useNavigationStore } from './navigation'

/**
 * 标签页项接口
 */
export interface TabItem {
  path: string
  title: string
  icon?: string
  affix?: boolean // 是否固定（不可关闭）
  query?: Record<string, any>
  params?: Record<string, any>
  fullPath?: string
}

export const useTabsStore = defineStore('tabs', () => {
  // 获取导航store
  const navigationStore = useNavigationStore()

  // 响应式状态
  const openTabs = ref<TabItem[]>([...navigationStore.affixTabs])
  const activeTabPath = ref(navigationStore.defaultActiveTabPath)

  // 计算属性
  const activeTab = computed(() => {
    return openTabs.value.find(tab => tab.path === activeTabPath.value)
  })

  const affixTabs = computed(() => {
    return navigationStore.affixTabs
  })

  const closableTabs = computed(() => {
    return openTabs.value.filter(tab => !tab.affix)
  })

  const tabsCount = computed(() => openTabs.value.length)

  const canCloseTab = computed(() => (path: string) => {
    const tab = openTabs.value.find(t => t.path === path)
    return tab && !tab.affix && openTabs.value.length > 1
  })

  /**
   * 添加标签页
   * @param route 路由信息
   */
  const addTab = (route: RouteLocationNormalized) => {
    const { path, meta, query, params, fullPath } = route

    // 过滤掉不应该显示在标签中的页面
    // 1. 隐藏的页面（如登录页、错误页等）
    if (meta?.hidden) {
      return
    }

    // 2. 排除特定的非菜单页面
    const excludedPaths = [
      '/login', // 登录页
      '/register', // 注册页
      '/forgot-password', // 忘记密码页
      '/404', // 404错误页
      '/403', // 403权限错误页
      '/500', // 500服务器错误页
      '/redirect' // 重定向页面
    ]

    if (excludedPaths.includes(path)) {
      return
    }

    // 3. 排除以特定前缀开头的页面
    const excludedPrefixes = ['/error', '/auth']
    if (excludedPrefixes.some(prefix => path.startsWith(prefix))) {
      return
    }

    // 4. 只有在导航配置中存在的页面才显示标签
    const navItem = navigationStore.getNavigationByPath(path)
    if (!navItem) {
      // 如果不在导航配置中，检查是否是特殊页面（如首页）
      const isSpecialPage = path === '/home' || path === '/' || path.startsWith('/home')
      if (!isSpecialPage) {
        return
      }
    }

    // 5. 对于hidden页面，如果在导航配置中存在，也允许显示标签
    // 这样可以确保像知识库详情、文档上传等页面能正常显示标签

    // 检查是否已存在
    const existingTab = openTabs.value.find(tab => tab.path === path)
    if (existingTab) {
      // 更新查询参数和路径参数
      existingTab.query = query
      existingTab.params = params
      existingTab.fullPath = fullPath
      activeTabPath.value = path
      return
    }

    // 创建新标签页
    const newTab: TabItem = {
      path,
      title: navItem?.title || (meta?.title as string) || '首页',
      icon: navItem?.icon || meta?.icon as string,
      affix: navItem?.affix || meta?.affix as boolean || false,
      query,
      params,
      fullPath
    }

    openTabs.value.push(newTab)
    activeTabPath.value = path
  }

  /**
   * 关闭标签页
   * @param path 标签页路径
   */
  const closeTab = (path: string) => {
    const index = openTabs.value.findIndex(tab => tab.path === path)
    if (index === -1) return

    const tab = openTabs.value[index]

    if (!tab) return

    // 固定标签页不能关闭
    if (tab.affix) return

    // 如果关闭的是当前活动标签页，需要切换到其他标签页
    if (path === activeTabPath.value) {
      // 优先切换到右侧标签页，如果没有则切换到左侧
      const nextTab = openTabs.value[index + 1] || openTabs.value[index - 1]
      if (nextTab) {
        activeTabPath.value = nextTab.path
      }
    }

    openTabs.value.splice(index, 1)
  }

  /**
   * 关闭其他标签页
   * @param path 保留的标签页路径
   */
  const closeOtherTabs = (path: string) => {
    openTabs.value = openTabs.value.filter(tab => tab.affix || tab.path === path)
    activeTabPath.value = path
  }

  /**
   * 关闭左侧标签页
   * @param path 基准标签页路径
   */
  const closeLeftTabs = (path: string) => {
    const index = openTabs.value.findIndex(tab => tab.path === path)
    if (index === -1) return

    const leftTabs = openTabs.value.slice(0, index)
    const removableTabs = leftTabs.filter(tab => !tab.affix)

    removableTabs.forEach(tab => {
      const tabIndex = openTabs.value.findIndex(t => t.path === tab.path)
      if (tabIndex > -1) {
        openTabs.value.splice(tabIndex, 1)
      }
    })
  }

  /**
   * 关闭右侧标签页
   * @param path 基准标签页路径
   */
  const closeRightTabs = (path: string) => {
    const index = openTabs.value.findIndex(tab => tab.path === path)
    if (index === -1) return

    const rightTabs = openTabs.value.slice(index + 1)
    const removableTabs = rightTabs.filter(tab => !tab.affix)

    removableTabs.forEach(tab => {
      const tabIndex = openTabs.value.findIndex(t => t.path === tab.path)
      if (tabIndex > -1) {
        openTabs.value.splice(tabIndex, 1)
      }
    })
  }

  /**
   * 关闭所有标签页
   */
  const closeAllTabs = () => {
    openTabs.value = [...navigationStore.affixTabs]
    // 切换到默认活动标签页
    activeTabPath.value = navigationStore.defaultActiveTabPath
  }

  /**
   * 设置活动标签页
   * @param path 标签页路径
   */
  const setActiveTab = (path: string) => {
    const tab = openTabs.value.find(t => t.path === path)
    if (tab) {
      activeTabPath.value = path
    }
  }

  /**
   * 刷新标签页
   * @param path 标签页路径
   */
  const refreshTab = (path?: string) => {
    const targetPath = path || activeTabPath.value
    if (!targetPath) return

    // 触发刷新事件，由组件层处理具体的路由操作
    // 这里只负责状态管理，不直接操作路由
    console.log('触发标签页刷新:', targetPath)

    // 可以在这里添加一些状态标记，比如刷新状态
    // 但具体的路由操作应该在组件中完成
  }

  /**
   * 获取标签页索引
   * @param path 标签页路径
   */
  const getTabIndex = (path: string): number => {
    return openTabs.value.findIndex(tab => tab.path === path)
  }

  /**
   * 检查标签页是否存在
   * @param path 标签页路径
   */
  const hasTab = (path: string): boolean => {
    return openTabs.value.some(tab => tab.path === path)
  }

  /**
   * 重置标签页状态
   */
  const resetTabs = () => {
    openTabs.value = [...navigationStore.affixTabs]
    activeTabPath.value = navigationStore.defaultActiveTabPath
  }

  return {
    // 状态
    openTabs,
    activeTabPath,

    // 计算属性
    activeTab,
    affixTabs,
    closableTabs,
    tabsCount,
    canCloseTab,

    // 方法
    addTab,
    closeTab,
    closeOtherTabs,
    closeLeftTabs,
    closeRightTabs,
    closeAllTabs,
    setActiveTab,
    refreshTab,
    getTabIndex,
    hasTab,
    resetTabs
  }
})
